import { CLASSES } from "../content/classes";
import { STATUSES } from "../content/statuses";
import { combat } from "../content/tuning";
import {
  afterDamage,
  afterHeal,
  beforeHeal,
  modifyIncoming,
  modifyOutgoing,
} from "./passives";
import { hasStatus, isActable, isPresent } from "./query";
import { applyStatus, interruptCast, tickStatusClock } from "./status";
import {
  type CombatEvent,
  type Cover,
  type DamageType,
  type ShieldLayer,
  type Unit,
  type World,
} from "./types";


export function nextId(world: World): number {
  world.nextId += 1;
  return world.nextId;
}

export function rand(world: World): number {
  let t = (world.rngState += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function pushEvent(
  world: World,
  partial: Omit<CombatEvent, "age" | "team" | "text" | "value"> & Partial<CombatEvent>,
): void {
  world.events.push({
    kind: partial.kind,
    x: partial.x,
    y: partial.y,
    value: partial.value ?? 0,
    team: partial.team ?? 0,
    text: partial.text ?? "",
    age: 0,
  });
  if (world.events.length > 56) world.events.splice(0, world.events.length - 56);
}

export interface DamageOpts {
  type?: DamageType;
  melee?: boolean;
  skillId?: string;
  tick?: boolean;
  echo?: boolean;
  guaranteedCrit?: boolean;
  critIf?: boolean;
}

export interface DamageResult {
  requested: number;
  dealt: number;
  toShield: number;
  toHp: number;
  crit: boolean;
}

export function applyDamage(
  world: World,
  target: Unit,
  amount: number,
  source: Unit | null,
  opts: DamageOpts = {},
): DamageResult {
  const empty: DamageResult = { requested: amount, dealt: 0, toShield: 0, toHp: 0, crit: false };
  if (!isPresent(target)) return empty;
  const type: DamageType = opts.type ?? "physical";
  if (type === "psychic" && hasStatus(target, "warded")) return empty;

  if (source && !opts.tick && !opts.echo && hasStatus(source, "blind")) {
    if (rand(world) < 0.5) {
      pushEvent(world, { kind: "miss", x: target.pos.x, y: target.pos.y - 0.35, text: "MISS", team: target.team });
      fireZeroCombat(world, target, source, opts);
      return empty;
    }
  }

  if (!opts.tick && !opts.echo && source && source.classId === "assassin" && consumeSwordBlock(target, source, world)) {
    return empty;
  }

  let dmg = Math.max(0, amount);
  let crit = !!opts.guaranteedCrit || !!opts.critIf;
  if (source && !opts.tick) {
    const out = modifyOutgoing(world, source, target, dmg, type, opts);
    dmg = out.amount;
    crit = crit || out.crit;
  }
  if (source && hasStatus(source, "maim")) dmg *= combat.maimOutMult;
  if (crit && !opts.tick) dmg *= combat.critMult;

  const inc = modifyIncoming(world, target, source, dmg, type, opts);
  dmg = Math.max(0, inc.amount);
  if (inc.blocked) {
    pushEvent(world, { kind: "block", x: target.pos.x, y: target.pos.y - 0.4, text: "BLOCK", team: target.team });
    fireZeroCombat(world, target, source, opts);
    return { requested: amount, dealt: 0, toShield: 0, toHp: 0, crit };
  }

  if (!opts.tick) dmg = Math.round(dmg);
  else dmg = Math.round(dmg * 1000) / 1000;

  const absorbed = absorbShield(target, dmg, type);
  let rest = dmg - absorbed;
  if (rest < 0) rest = 0;
  if (rest > 0 && !opts.tick) rest = Math.round(rest);
  target.hp -= rest;
  const dealt = absorbed + rest;
  target.damageTaken += dealt;

  if (source && !opts.tick) {
    source.damageDealt += dealt;
  }

  if (!opts.tick && (dealt > 0 || amount >= 0)) {
    pushEvent(world, {
      kind: crit ? "crit" : "dmg",
      x: target.pos.x,
      y: target.pos.y - 0.45,
      value: Math.max(0, Math.round(dealt)),
      team: target.team,
      text: crit ? `CRIT ${Math.round(dealt)}` : "",
    });
  }

  world.hookDepth += 1;
  afterDamage(world, target, source, {
    requested: amount,
    dealt,
    toShield: absorbed,
    toHp: rest,
    crit,
    type,
    tick: !!opts.tick,
    echo: !!opts.echo,
    skillId: opts.skillId,
  });
  world.hookDepth -= 1;

  if (target.hp <= 0) downUnit(world, target, source);
  return { requested: amount, dealt, toShield: absorbed, toHp: rest, crit };
}

function fireZeroCombat(world: World, target: Unit, source: Unit | null, opts: DamageOpts): void {
  world.hookDepth += 1;
  afterDamage(world, target, source, {
    requested: 0,
    dealt: 0,
    toShield: 0,
    toHp: 0,
    crit: false,
    type: opts.type ?? "physical",
    tick: !!opts.tick,
    echo: !!opts.echo,
    skillId: opts.skillId,
  });
  world.hookDepth -= 1;
}

function consumeSwordBlock(target: Unit, source: Unit, world: World): boolean {
  if (target.classId !== "assassin") return false;
  if ((target.state.weapon ?? 0) !== 1) return false;
  if ((target.state.blockReady ?? 1) < 1) return false;
  target.state.blockReady = 0;
  target.state.blockCd = 3;
  pushEvent(world, { kind: "block", x: target.pos.x, y: target.pos.y - 0.4, text: "BLOCK", team: target.team });
  void source;
  return true;
}

function absorbShield(target: Unit, amount: number, type: DamageType): number {
  if (amount <= 0) return 0;
  let left = amount;
  let absorbed = 0;
  for (const s of target.shields) {
    if (s.amount <= 0) continue;
    if (s.kind === "psychic" && type !== "psychic") continue;
    const take = Math.min(s.amount, left);
    s.amount -= take;
    absorbed += take;
    left -= take;
    if (left <= 0) break;
  }
  target.shields = target.shields.filter((s) => s.amount > 0.001);
  return absorbed;
}

export function addShield(
  target: Unit,
  amount: number,
  opts: {
    kind?: ShieldLayer["kind"];
    max?: number;
    decay?: number;
    regen?: number;
    sourceId?: number;
    tag?: string;
    id?: string;
  } = {},
): void {
  const tag = opts.tag ?? "";
  const kind = opts.kind ?? "any";
  const existing = tag ? target.shields.find((s) => s.tag === tag && s.kind === kind) : undefined;
  if (existing) {
    existing.amount = Math.min(opts.max ?? existing.max, existing.amount + amount);
    existing.max = Math.max(existing.max, opts.max ?? existing.max);
    if (opts.decay !== undefined) existing.decayPerSec = opts.decay;
    if (opts.regen !== undefined) existing.regenPerSec = opts.regen;
    return;
  }
  target.shields.push({
    id: opts.id ?? `${kind}-${tag || "s"}`,
    kind,
    amount,
    max: opts.max ?? amount,
    decayPerSec: opts.decay ?? 0,
    regenPerSec: opts.regen ?? 0,
    sourceId: opts.sourceId ?? -1,
    tag,
  });
}

export interface HealOpts {
  tick?: boolean;
  skillId?: string;
  echo?: boolean;
}

export function applyHeal(
  world: World,
  target: Unit,
  amount: number,
  source: Unit | null,
  opts: HealOpts = {},
): number {
  if (!isPresent(target) || amount <= 0) return 0;
  const converted = beforeHeal(world, target, source, amount, opts);
  if (converted.damage) {
    applyDamage(world, target, converted.amount, source, {
      type: "physical",
      skillId: opts.skillId ?? "penance",
      echo: true,
    });
    return 0;
  }
  let h = converted.amount;
  if (hasStatus(target, "wound")) h *= combat.woundHealMult;
  if (!opts.tick) h = Math.round(h);
  if (target.kind === "corpse") {
    h = Math.min(target.maxHp - target.hp, h);
  } else {
    h = Math.min(target.maxHp - target.hp, h);
  }
  if (h <= 0) return 0;
  target.hp += h;
  if (source) source.healsDone += h;
  if (!opts.tick) {
    pushEvent(world, { kind: "heal", x: target.pos.x, y: target.pos.y - 0.45, value: Math.round(h), team: target.team });
  }
  afterHeal(world, target, source, h, opts);
  return h;
}

export function stealHealth(world: World, from: Unit, to: Unit, amount: number, source: Unit): number {
  const taken = applyDamage(world, from, amount, source, { type: "physical", skillId: "steal", echo: true });
  if (taken.toHp > 0 || taken.toShield > 0) {
    const gain = Math.max(1, Math.round(taken.toHp || amount));
    if (to.kind === "corpse") to.hp = Math.min(to.maxHp, to.hp + gain);
    else applyHeal(world, to, gain, source, { echo: true, skillId: "steal" });
    return gain;
  }
  return 0;
}

export function downUnit(world: World, unit: Unit, _source: Unit | null): void {
  if (unit.kind === "spirit" || unit.kind === "minion") {
    unit.hp = 0;
    unit.dead = true;
    unit.casting = null;
    unit.charging = null;
    unit.statuses = [];
    unit.shields = [];
    pushEvent(world, { kind: "kd", x: unit.pos.x, y: unit.pos.y - 0.55, text: "DOWN", team: unit.team });
    return;
  }
  if (unit.classId === "cultist" && (unit.state.selfCorpse ?? 0) !== 1 && unit.kind === "hero") {
    unit.kind = "corpse";
    unit.dead = false;
    unit.hp = combat.corpseHp;
    unit.maxHp = combat.corpseHp;
    unit.state.selfCorpse = 1;
    unit.casting = null;
    unit.charging = null;
    unit.shields = [];
    unit.statuses = unit.statuses.filter((s) => STATUSES[s.id].polarity === "condition");
    applyStatus(world, unit, "cripple", 99, unit, { silent: true });
    unit.respawnIn = unit.respawnSecs;
    pushEvent(world, { kind: "info", x: unit.pos.x, y: unit.pos.y - 0.6, text: "CORPSE", team: unit.team });
    return;
  }
  unit.hp = unit.kind === "corpse" ? 0 : 0;
  if (unit.kind === "hero") {
    unit.kind = "corpse";
    unit.hp = combat.corpseHp;
    unit.maxHp = combat.corpseHp;
    unit.dead = true;
    unit.respawnIn = unit.respawnSecs;
    unit.casting = null;
    unit.charging = null;
    unit.statuses = [];
    unit.shields = [];
    unit.lockout = 0;
    pushEvent(world, { kind: "kd", x: unit.pos.x, y: unit.pos.y - 0.6, text: "DOWNED", team: unit.team });
    return;
  }
  unit.dead = true;
  unit.hp = 0;
  unit.casting = null;
  unit.charging = null;
  unit.statuses = [];
  unit.shields = [];
  pushEvent(world, { kind: "kd", x: unit.pos.x, y: unit.pos.y - 0.6, text: "DESTROYED", team: unit.team });
}

export function destroyCorpse(world: World, unit: Unit): void {
  unit.hp = 0;
  if (unit.state.selfCorpse === 1) {
    unit.dead = true;
    unit.state.selfCorpse = 0;
    unit.kind = "corpse";
    unit.respawnIn = Math.max(unit.respawnIn, 4);
  } else {
    unit.hp = 0;
    unit.kind = "corpse";
    unit.dead = true;
  }
  pushEvent(world, { kind: "info", x: unit.pos.x, y: unit.pos.y - 0.5, text: "HARVEST", team: unit.team });
}

export function reviveUnit(unit: Unit): void {
  const def = CLASSES[unit.classId];
  unit.dead = false;
  unit.kind = unit.classId === "spirit" ? "spirit" : unit.classId === "skeleton" || unit.classId === "golem" ? "minion" : "hero";
  unit.maxHp = def.maxHp;
  unit.hp = def.maxHp;
  unit.pos = { x: unit.spawn.x, y: unit.spawn.y };
  unit.prevPos = { x: unit.spawn.x, y: unit.spawn.y };
  unit.facing = unit.team === 0 ? 0 : Math.PI;
  unit.casting = null;
  unit.charging = null;
  unit.statuses = [];
  unit.shields = [];
  unit.lockout = 0.2;
  unit.respawnIn = 0;
  unit.state.selfCorpse = 0;
  if (unit.classId === "paladin") unit.state.charge = 0;
}

export function tickShields(unit: Unit, dt: number): void {
  for (const s of unit.shields) {
    if (s.decayPerSec > 0) s.amount -= s.decayPerSec * dt;
    if (s.regenPerSec > 0) s.amount = Math.min(s.max, s.amount + s.regenPerSec * dt);
  }
  unit.shields = unit.shields.filter((s) => s.amount > 0.001);
}

export function tickUnitClock(world: World, unit: Unit, dt: number): void {
  if (unit.dead && unit.kind === "corpse" && unit.classId !== "cultist") {
    unit.respawnIn -= dt;
    if (unit.respawnIn <= 0 && CLASSES[unit.classId].playable) reviveUnit(unit);
    return;
  }
  if (unit.dead && unit.state.selfCorpse !== 1) {
    if (CLASSES[unit.classId].playable) {
      unit.respawnIn -= dt;
      if (unit.respawnIn <= 0) reviveUnit(unit);
    }
    return;
  }
  if (unit.kind === "corpse" && unit.state.selfCorpse === 1) {
    unit.respawnIn -= dt;
    if (unit.respawnIn <= 0) reviveUnit(unit);
  }

  unit.basicCd = Math.max(0, unit.basicCd - dt);
  const skillRate = unit.classId === "ranger" && unit.stillFor >= 0.12 ? 2 : 1;
  unit.skillCd = Math.max(0, unit.skillCd - dt * skillRate);
  unit.ultCd = Math.max(0, unit.ultCd - dt);
  unit.lockout = Math.max(0, unit.lockout - dt);
  if (unit.casting) unit.casting.remaining -= dt;
  if (unit.state.blockCd !== undefined) {
    unit.state.blockCd = Math.max(0, unit.state.blockCd - dt);
    if (unit.state.blockCd <= 0) unit.state.blockReady = 1;
  }
  for (const k of Object.keys(unit.ambushGrace)) {
    unit.ambushGrace[+k] = Math.max(0, unit.ambushGrace[+k] - dt);
  }

  const due = tickStatusClock(unit, dt);
  for (const s of due) {
    const def = STATUSES[s.id];
    const src = world.units.find((u) => u.id === s.sourceId) ?? null;
    if (def.tickDamage > 0) {
      applyDamage(world, unit, def.tickDamage, src, { type: def.tickType, tick: true, skillId: s.id });
    }
    if (def.tickHeal > 0) {
      applyHeal(world, unit, def.tickHeal, src, { tick: true, skillId: s.id });
    }
  }

  tickShields(unit, dt);
}

export function damageCover(_world: World, cover: Cover, amount: number): void {
  if (cover.destroyed || amount <= 0) return;
  cover.hp -= amount;
  if (cover.hp <= 0) {
    cover.hp = 0;
    cover.destroyed = true;
  }
}

export function liveCover(world: World): Cover[] {
  return world.cover.filter((c) => !c.destroyed);
}

export { interruptCast, isActable, applyStatus };
